(() => {
  const ELEMENT_NAME = 'product-addon-options';

  if (customElements.get(ELEMENT_NAME)) return;

  class ProductAddons extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedAndReady) return;

      this.isConnectedAndReady = true;
      this.inputs = Array.from(this.querySelectorAll('[data-addon-input]'));
      this.variantInputs = Array.from(this.querySelectorAll('[data-addon-variant-input]'));
      this.captureSetConfig();
      this.captureAddonConfigs();
      this.errorElement = this.querySelector('.product-addons__error');
      this.statusElement = this.querySelector('.product-addons__status');
      this.handleChange = this.handleChange.bind(this);
      this.handleFormContextChange = this.handleFormContextChange.bind(this);
      this.parentForm = this.findParentForm();
      this.quantityInput = this.getFormControl('quantity');
      this.parentVariantColors = this.parseParentVariantColors();

      this.addEventListener('change', this.handleChange);
      this.quantityInput?.addEventListener('input', this.handleFormContextChange);
      this.quantityInput?.addEventListener('change', this.handleFormContextChange);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.syncGiftColors();
          this.resetAcceleratedCheckoutGroupId();
          this.syncAcceleratedCheckoutFields();
        });
      }

      this.syncGiftColors();
      this.syncCards();
      this.syncAcceleratedCheckoutFields();

      this.dispatchEvent(
        new CustomEvent('product-addons:ready', {
          bubbles: true,
          detail: { productFormId: this.dataset.productFormId },
        })
      );
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.quantityInput?.removeEventListener('input', this.handleFormContextChange);
      this.quantityInput?.removeEventListener('change', this.handleFormContextChange);
      this.variantChangeUnsubscriber?.();
      // Clear only the form owned by this instance. During a Dawn product swap,
      // a new form can briefly share the same ID before this element disconnects.
      this.clearAcceleratedCheckoutFields(this.parentForm);
      this.resetAcceleratedCheckoutGroupId(this.parentForm);
      this.isConnectedAndReady = false;
      this.getSiblingAddonGroups(this.parentForm)[0]?.syncAcceleratedCheckoutFields();
      this.parentForm = null;
    }

    /**
     * Returns only the public add-on contract consumed by ProductForm.
     * @param {number|string} parentQuantity
     * @returns {{id: string, quantity: number, kind: string, quantityPolicy: string, productId: string, label: string}[]}
     */
    getSelectedAddons(parentQuantity = 1) {
      const normalizedParentQuantity = this.toPositiveInteger(parentQuantity, 1);

      return this.getSelectedInputs()
        .slice(0, this.maximumSelection)
        .map((input) => {
          const config = this.addonConfigs.get(input);

          return {
            id: config.variantId,
            quantity: config.quantityPolicy === 'fixed_one' ? 1 : normalizedParentQuantity,
            kind: config.kind,
            quantityPolicy: config.quantityPolicy,
            productId: config.productId,
            label: config.label,
            configurationId: String(this.dataset.addonSetId || ''),
            setName: String(this.dataset.addonSetName || ''),
          };
        });
    }

    get hasSelection() {
      return this.getSelectedInputs().length > 0;
    }

    setLoading(isLoading) {
      const loading = Boolean(isLoading);

      this.toggleAttribute('aria-busy', loading);
      this.classList.toggle('product-addons--loading', loading);

      this.inputs.forEach((input) => {
        input.disabled = loading || !this.addonConfigs.get(input)?.available;
      });

      this.variantInputs.forEach((input) => {
        input.disabled = loading || input.dataset.available !== 'true';
      });
    }

    showError(message) {
      if (!this.errorElement) return;

      const errorMessage = typeof message === 'string' ? message.trim() : '';
      this.errorElement.textContent = errorMessage;
      this.errorElement.hidden = errorMessage.length === 0;
      this.classList.toggle('product-addons--has-error', errorMessage.length > 0);
    }

    clearError() {
      this.showError('');
    }

    focusFirstAvailable() {
      this.inputs.find((input) => this.addonConfigs.get(input)?.available && !input.disabled)?.focus();
    }

    handleChange(event) {
      const changedVariant = event.target.closest('[data-addon-variant-input]');
      if (changedVariant && this.contains(changedVariant)) {
        this.handleVariantChange(changedVariant);
        return;
      }

      const changedInput = event.target.closest('[data-addon-input]');
      if (!changedInput || !this.contains(changedInput)) return;
      if (!this.addonConfigs.has(changedInput)) return;

      if (this.selectionMode === 'single' && changedInput.checked) {
        this.inputs.forEach((input) => {
          if (input !== changedInput) input.checked = false;
        });
      }

      if (this.selectionMode === 'multiple' && this.getSelectedInputs().length > this.maximumSelection) {
        changedInput.checked = false;
        this.showError(`You can select up to ${this.maximumSelection} add-ons.`);
        this.announce(`Maximum of ${this.maximumSelection} add-ons reached.`);
      } else {
        this.clearError();
        const label = this.addonConfigs.get(changedInput).label || 'Add-on';
        this.announce(`${label} ${changedInput.checked ? 'selected' : 'removed'}.`);
      }

      this.syncCards();
      this.resetAcceleratedCheckoutGroupId();
      this.syncAcceleratedCheckoutFields();

      this.dispatchEvent(
        new CustomEvent('product-addons:change', {
          bubbles: true,
          detail: {
            addons: this.getSelectedAddons(this.getParentQuantity()),
            hasSelection: this.hasSelection,
          },
        })
      );
    }

    handleVariantChange(variantInput) {
      if (!variantInput.checked || variantInput.dataset.available !== 'true') return;

      const card = variantInput.closest('[data-addon-card]');
      const addonInput = card?.querySelector('[data-addon-input]');
      if (!card || !addonInput) return;

      addonInput.value = variantInput.dataset.variantId || '';
      addonInput.dataset.variantId = variantInput.dataset.variantId || '';
      addonInput.dataset.kind = variantInput.dataset.kind || 'paid_addon';
      addonInput.dataset.quantityPolicy = variantInput.dataset.quantityPolicy || 'match_parent_quantity';
      addonInput.dataset.available = variantInput.dataset.available || 'false';
      addonInput.disabled = variantInput.dataset.available !== 'true';

      this.captureAddonConfig(addonInput);
      this.updateCardVariant(card, variantInput);
      this.clearError();
      this.syncCards();
      this.resetAcceleratedCheckoutGroupId();
      this.syncAcceleratedCheckoutFields();

      const label = this.addonConfigs.get(addonInput)?.label || 'Add-on';
      const variantTitle = variantInput.dataset.variantTitle || 'option';
      this.announce(`${label}, ${variantTitle} selected.`);

      this.dispatchEvent(
        new CustomEvent('product-addons:change', {
          bubbles: true,
          detail: {
            addons: this.getSelectedAddons(this.getParentQuantity()),
            hasSelection: this.hasSelection,
          },
        })
      );
    }

    updateCardVariant(card, variantInput) {
      const isFreeGift = variantInput.dataset.isFree === 'true';
      const selectedVariant = card.querySelector('[data-addon-selected-variant]');
      const price = card.querySelector('.product-addon-card__price');
      const priceLabel = card.querySelector('[data-addon-price-label]');
      const comparePrice = card.querySelector('[data-addon-compare-price]');
      const availability = card.querySelector('[data-addon-availability]');
      const image = card.querySelector('.product-addon-card__image');
      const promotion = card.querySelector('[data-addon-dynamic-promotion]');

      if (selectedVariant) selectedVariant.textContent = variantInput.dataset.variantTitle || '';
      if (priceLabel) {
        priceLabel.textContent = isFreeGift ? 'Free Gift' : variantInput.dataset.priceFormatted || '';
      }
      price?.classList.toggle('product-addon-card__price--free-gift', isFreeGift);

      if (comparePrice) {
        comparePrice.textContent = variantInput.dataset.comparePriceFormatted || '';
        comparePrice.hidden = variantInput.dataset.showCompare !== 'true';
      }

      if (availability) availability.hidden = variantInput.dataset.available === 'true';
      if (price) price.hidden = variantInput.dataset.available !== 'true';
      card.classList.toggle('product-addon-card--unavailable', variantInput.dataset.available !== 'true');

      if (promotion) promotion.textContent = isFreeGift ? 'Claim free gift' : 'Add premium option';

      if (image && variantInput.dataset.imageSrc) {
        image.src = variantInput.dataset.imageSrc;
        if (variantInput.dataset.imageSrcset) image.srcset = variantInput.dataset.imageSrcset;
        image.alt = variantInput.dataset.imageAlt || '';
      }

      card.querySelectorAll('[data-addon-details-link]').forEach((link) => {
        if (variantInput.dataset.detailsUrl) link.href = variantInput.dataset.detailsUrl;
      });
    }

    get selectionMode() {
      return this.setConfig?.selectionMode || 'single';
    }

    get maximumSelection() {
      return this.setConfig?.maximumSelection || 1;
    }

    get required() {
      return this.setConfig?.required || false;
    }

    getSelectedInputs() {
      return this.inputs.filter((input) => input.checked && this.addonConfigs.get(input)?.available);
    }

    captureAddonConfigs() {
      this.addonConfigs = new WeakMap();
      this.inputs.forEach((input) => this.captureAddonConfig(input));
    }

    captureAddonConfig(input) {
      const variantId = String(input.dataset.variantId || '').trim();
      const productId = String(input.dataset.productId || '').trim();

      if (!/^\d+$/.test(variantId) || !/^\d+$/.test(productId)) {
        input.disabled = true;
        return;
      }

      this.addonConfigs.set(
        input,
        Object.freeze({
          variantId,
          productId,
          kind: input.dataset.kind === 'free_gift' ? 'free_gift' : 'paid_addon',
          quantityPolicy:
            input.dataset.quantityPolicy === 'fixed_one' ? 'fixed_one' : 'match_parent_quantity',
          label: input.dataset.label || '',
          available: input.dataset.available === 'true',
        })
      );
    }

    captureSetConfig() {
      const selectionMode = this.dataset.selectionMode === 'multiple' ? 'multiple' : 'single';

      this.setConfig = Object.freeze({
        selectionMode,
        maximumSelection:
          selectionMode === 'single' ? 1 : this.toPositiveInteger(this.dataset.maximumSelection, 1),
        required: this.dataset.required === 'true',
      });
    }

    findParentForm() {
      const formId = this.dataset.productFormId;
      return formId ? document.getElementById(formId) : null;
    }

    getParentForm() {
      if (this.parentForm?.isConnected) return this.parentForm;

      this.parentForm = this.findParentForm();
      return this.parentForm;
    }

    getFormControl(name) {
      const form = this.getParentForm();
      return form?.elements?.namedItem?.(name) || null;
    }

    getParentQuantity() {
      const quantityInput = this.getFormControl('quantity');
      return this.toPositiveInteger(quantityInput?.value, 1);
    }

    getParentVariantId() {
      const variantId = String(this.getFormControl('id')?.value || '').trim();
      return /^\d+$/.test(variantId) ? variantId : '';
    }

    parseParentVariantColors() {
      try {
        return JSON.parse(this.querySelector('[data-addon-parent-colors]')?.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    // Free gifts offered in several colors follow the parent variant's color (gift-color-match.js).
    // Only a new parent variant triggers it, so a gift color picked by hand survives unrelated events.
    syncGiftColors() {
      const parentVariantId = this.getParentVariantId();
      if (!parentVariantId || parentVariantId === this.giftColorVariantId) return;

      this.giftColorVariantId = parentVariantId;
      window.HeroForceGiftColor?.selectMatchingGifts(this, this.parentVariantColors[parentVariantId]);
    }

    toPositiveInteger(value, fallback) {
      const parsedValue = Number.parseInt(value, 10);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
    }

    syncCards() {
      this.inputs.forEach((input) => {
        const card = input.closest('.product-addon-card');
        card?.classList.toggle('product-addon-card--selected', input.checked);
      });
    }

    handleFormContextChange() {
      this.syncAcceleratedCheckoutFields();
    }

    syncAcceleratedCheckoutFields() {
      const form = this.getParentForm();
      if (!form) return;

      this.clearAcceleratedCheckoutFields(form);

      const addonGroups = this.getSiblingAddonGroups(form);

      // Required sets intentionally don't render accelerated checkout because
      // Shopify's wallet UI can't enforce the required-selection message.
      if (addonGroups.some((addonGroup) => addonGroup.required)) {
        this.resetAcceleratedCheckoutGroupId(form);
        return;
      }

      const parentVariantId = this.getParentVariantId();
      if (!parentVariantId) return;

      const parentQuantity = this.getParentQuantity();
      const selectedAddons = addonGroups.flatMap((addonGroup) =>
        addonGroup.getSelectedAddons(parentQuantity)
      );
      if (selectedAddons.length === 0) {
        this.resetAcceleratedCheckoutGroupId(form);
        return;
      }

      form.__heroForceAddonCheckoutGroupId ||= this.createAddonGroupId();

      this.buildAcceleratedCheckoutFields(
        selectedAddons,
        parentVariantId,
        form.__heroForceAddonCheckoutGroupId
      ).forEach(({ name, value }) => {
        const input = (form.ownerDocument || document).createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-addon-accelerated-checkout-field', '');
        form.appendChild(input);
      });
    }

    buildAcceleratedCheckoutFields(selectedAddons, parentVariantId, groupId) {
      const configurationIds = this.getUniqueAddonValues(selectedAddons, 'configurationId');
      const addonSetNames = this.getUniqueAddonValues(selectedAddons, 'setName');
      const configurationId = configurationIds.join(',') || String(this.dataset.addonSetId || groupId);
      const addonSetName = addonSetNames.join(' + ') || String(this.dataset.addonSetName || '');
      const fields = [
        { name: 'properties[_addon_group_id]', value: groupId },
        { name: 'properties[_addon_role]', value: 'parent' },
        { name: 'properties[_addon_configuration_id]', value: configurationId },
        { name: 'properties[_addon_set_name]', value: addonSetName },
        { name: 'properties[_addon_expected_child_count]', value: String(selectedAddons.length) },
      ];

      selectedAddons.forEach((addon, addonIndex) => {
        const index = addonIndex + 1;
        const isFree = addon.kind === 'free_gift' || String(addon.price) === '0';
        const childProperties = [
          { key: '_addon_group_id', value: groupId },
          { key: '_addon_role', value: 'child' },
          { key: '_addon_kind', value: String(addon.kind || (isFree ? 'free_gift' : 'paid_addon')) },
          {
            key: '_addon_quantity_policy',
            value: String(addon.quantityPolicy || (isFree ? 'fixed_one' : 'match_parent_quantity')),
          },
          { key: '_addon_parent_variant_id', value: parentVariantId },
          { key: '_addon_label', value: String(addon.label || '') },
          {
            key: '_addon_configuration_id',
            value: String(addon.configurationId || configurationId),
          },
          { key: '_addon_set_name', value: String(addon.setName || addonSetName) },
        ];

        if (isFree) {
          childProperties.push({ key: '_gift_of', value: parentVariantId });
          childProperties.push({ key: '_is_gift', value: 'true' });
        } else {
          childProperties.push({ key: '_upsell_of', value: parentVariantId });
        }

        fields.push(
          { name: `${prefix}[id]`, value: String(addon.id) },
          { name: `${prefix}[quantity]`, value: String(addon.quantity) },
          { name: `${prefix}[parent_id]`, value: parentVariantId },
          { name: `${prefix}[properties]`, value: JSON.stringify(childProperties) }
        );
      });

      return fields;
    }

    getUniqueAddonValues(selectedAddons, propertyName) {
      return Array.from(
        new Set(
          selectedAddons
            .map((addon) => String(addon?.[propertyName] || '').trim())
            .filter(Boolean)
        )
      );
    }

    getSiblingAddonGroups(form = this.getParentForm()) {
      const formId = form?.getAttribute?.('id') || this.dataset.productFormId;
      if (!formId) return [];

      return Array.from(
        (form.ownerDocument || document).querySelectorAll(
          'product-addon-options[data-product-form-id]'
        )
      ).filter(
        (addonGroup) =>
          addonGroup.isConnectedAndReady && addonGroup.dataset.productFormId === formId
      );
    }

    resetAcceleratedCheckoutGroupId(form = this.getParentForm()) {
      if (form) delete form.__heroForceAddonCheckoutGroupId;
    }

    clearAcceleratedCheckoutFields(form = this.getParentForm()) {
      form
        ?.querySelectorAll('[data-addon-accelerated-checkout-field]')
        .forEach((field) => field.remove());
    }

    createAddonGroupId() {
      const cryptoApi = window.crypto;
      if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

      if (cryptoApi?.getRandomValues) {
        const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }

      return `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }

    announce(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      window.requestAnimationFrame(() => {
        this.statusElement.textContent = message;
      });
    }
  }

  customElements.define(ELEMENT_NAME, ProductAddons);
})();
